import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MapPin, Calendar, Clock, Target, ArrowRight, ArrowLeft, Award, Heart, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import Countdown from '../components/Countdown';
import LatestUpdates from '../components/LatestUpdates';
import { eventInfo } from '../mock';

const Home = () => {
  const [committee, setCommittee] = useState([]);
  const memoriesRef = useRef(null);
  const navigate = useNavigate();

  const scrollMemories = (direction) => {
    const el = memoriesRef.current;
    if (!el) return;
    const scrollAmount = el.clientWidth * 0.75;
    el.scrollBy({ left: direction === 'right' ? scrollAmount : -scrollAmount, behavior: 'smooth' });
  };

  useEffect(() => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API_URL}/api/committee`)
      .then((res) => res.json())
      .then((data) => setCommittee(Array.isArray(data) ? data : []))
      .catch(() => setCommittee([]));
  }, []);

  const features = [
    {
      icon: Award,
      title: "Hero Dog Celebrations",
      description: "Enter competitions for Twin Waters Everyday Hero and Best Dog Dad",
      image: "https://customer-assets.emergentagent.com/job_comp-central-1/artifacts/nw0ajwhj_IMG_2029.jpeg",
      imagePos: "object-center"
    },
    {
      icon: Heart,
      title: "Community Gathering",
      description: "Paws in the Park is more than just an event—it's a celebration of the incredible working dogs, therapy dogs, and service dogs that make our community safer, healthier, and happier",
      image: "https://customer-assets.emergentagent.com/job_comp-central-1/artifacts/903k8kl2_2B6A3ACB-78EC-4CA0-A0CB-8C7AA66690DF_1_105_c.jpeg",
      imagePos: "object-center"
    },
    {
      icon: Sparkles,
      title: "Hero Masters",
      description: "From assistance dogs to wildlife detection dogs, retired police K9s to therapy companions, we're bringing together diverse canine heroes and the organizations that support them",
      image: "https://customer-assets.emergentagent.com/job_comp-central-1/artifacts/0hl67p5b_E7F1C4A2-50CE-45E6-9580-4A6BBDC773B8_1_105_c.jpeg",
      imagePos: "object-bottom"
    },
    {
      icon: Target,
      title: "Fun Activities",
      description: "Musical Sits, Red Light Green Light, and Paws of Purpose Kids Colouring Contest—plus food for you and your fur family, and pet-friendly photo booth",
      image: "https://customer-assets.emergentagent.com/job_comp-central-1/artifacts/48dn3cif_BA3C08D0-5BD7-402B-8C2D-6BC95CCB2E2F_1_105_c.jpeg",
      imagePos: "object-center"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Latest Updates ticker — top of homepage only */}
      <LatestUpdates />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-cyan-50/40 via-white to-white">
        <div className="relative container mx-auto px-4 pt-20 pb-10 md:pt-28 md:pb-12">
          <div className="max-w-5xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-slate-900 mb-6 tracking-tight leading-[1.05]">
              <div className="mb-2">Twin Waters Residents Association</div>
              <div>Paws in the Park 2026</div>
            </h1>
            <p className="text-xl md:text-2xl text-cyan-700 font-semibold mb-2">
              {eventInfo.theme}
            </p>
            <p className="text-lg md:text-xl text-slate-600 font-medium italic mb-10">
              {eventInfo.subtitle}
            </p>

            {/* Event Mascot — large, between subtitle and intro paragraph */}
            <div className="flex justify-center mb-10">
              <img
                src="https://customer-assets.emergentagent.com/job_comp-central-1/artifacts/odzfgbhg_6C8C9223-7AA2-452C-9B0A-BD26586B09F3%20copy.png"
                alt="Paws for Purpose TWRA — Paws in the Park 2026 mascot"
                className="w-72 sm:w-80 md:w-96 lg:w-[28rem] h-auto drop-shadow-2xl"
              />
            </div>

            <p className="text-sm md:text-base text-slate-600 mb-8 max-w-3xl mx-auto leading-relaxed">
              This year we will celebrate dogs who serve, comfort, and protect our community. 
              From loyal companions and everyday best friends to service animals, therapy dogs, 
              and conservation work, this celebration highlights the many ways dogs support, 
              comfort, and inspire us. Through a themed parade, storytelling, and special awards, 
              we come together to recognise the unconditional love, courage, and devotion our 
              four-legged heroes bring to our homes and our community every day.
            </p>

            {/* Event Quick Info — all on one line (forced no-wrap on larger screens) */}
            <div className="flex flex-nowrap items-center justify-center gap-x-3 sm:gap-x-6 md:gap-x-10 mb-10 text-slate-900 whitespace-nowrap overflow-x-auto hide-scrollbar">
              <div className="flex items-center space-x-2 flex-shrink-0">
                <MapPin className="h-4 w-4 md:h-5 md:w-5 text-cyan-600" />
                <span className="text-xs sm:text-sm md:text-lg font-bold">{eventInfo.location}</span>
              </div>
              <span className="text-slate-300 flex-shrink-0">|</span>
              <div className="flex items-center space-x-2 flex-shrink-0">
                <Calendar className="h-4 w-4 md:h-5 md:w-5 text-cyan-600" />
                <span className="text-xs sm:text-sm md:text-lg font-bold">September 6, 2026</span>
              </div>
              <span className="text-slate-300 flex-shrink-0">|</span>
              <div className="flex items-center space-x-2 flex-shrink-0">
                <Clock className="h-4 w-4 md:h-5 md:w-5 text-cyan-600" />
                <span className="text-xs sm:text-sm md:text-lg font-bold">11am – 2pm</span>
              </div>
            </div>

            <div className="flex flex-wrap justify-center gap-4 mb-14">
              <Button asChild size="lg" className="bg-gradient-to-r from-cyan-600 to-teal-600 hover:from-cyan-700 hover:to-teal-700 text-lg px-8">
                <Link to="/performers">
                  Hero Masters
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="text-lg px-8 border-cyan-600 text-cyan-700 hover:bg-cyan-50">
                <Link to="/vendors">Vendors</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="text-lg px-8 border-cyan-600 text-cyan-700 hover:bg-cyan-50">
                <Link to="/competitions">Competitions</Link>
              </Button>
              {/* Event Etiquette — fancy enticing CTA */}
              <Button
                asChild
                size="lg"
                className="text-lg px-8 bg-gradient-to-r from-amber-400 via-pink-400 to-rose-500 hover:from-amber-500 hover:via-pink-500 hover:to-rose-600 text-white border-0 shadow-md hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 group"
              >
                <Link to="/etiquette" className="flex items-center">
                  <Sparkles className="mr-2 h-5 w-5 group-hover:rotate-12 transition-transform" />
                  Event Etiquette
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
            </div>

            {/* Event Countdown — moved here, right under the buttons */}
            <div className="max-w-4xl mx-auto">
              <Countdown targetDate={eventInfo.date} />
            </div>

            {/* Event promo video — plays inline, muted by default so hitting play doesn't jump-scare visitors */}
            <div className="max-w-4xl mx-auto mt-8">
              <div className="relative rounded-2xl overflow-hidden shadow-lg border border-slate-200 bg-black">
                <video
                  src="https://customer-assets-lqy194kg.emergentagent.net/wingman/557fe86f-78a9-4c02-866c-8df432d2818c/attachments/397397419a3340dba2e74bd5c9e980b8_paws-in-the-park-16x9-1080p.mp4"
                  poster="https://customer-assets-lqy194kg.emergentagent.net/wingman/557fe86f-78a9-4c02-866c-8df432d2818c/attachments/c8c4c1057cdb4690bf95747731f46b2a_paws-facebook-banner-1920x1080.jpg"
                  controls
                  playsInline
                  preload="metadata"
                  className="w-full h-auto block"
                  data-testid="home-hero-video"
                >
                  Your browser doesn't support embedded video. <a href="https://customer-assets-lqy194kg.emergentagent.net/wingman/557fe86f-78a9-4c02-866c-8df432d2818c/attachments/397397419a3340dba2e74bd5c9e980b8_paws-in-the-park-16x9-1080p.mp4" className="underline">Download the promo video</a>.
                </video>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sponsors Section */}
      <section className="pt-2 pb-8 bg-white border-b border-slate-200">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-6">
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3 tracking-tight">
                Our Generous Sponsors
              </h2>
              <p className="text-base md:text-lg text-slate-600 max-w-3xl mx-auto">
                We are deeply grateful to our sponsors whose generous support makes Paws in the Park possible.
                Their commitment to our community and dedication to celebrating the incredible bond between people
                and their dogs helps us create a memorable day for everyone. Thank you for believing in our mission
                and helping us bring this special event to life.
              </p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 items-center">
              {/* Joanne Reilly Property */}
              <a 
                href="https://joannereillyproperty.com.au/contact/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center justify-center p-5 bg-slate-50 rounded-xl border border-slate-200 hover:border-cyan-300 hover:shadow-lg transition-all duration-300 h-44"
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_comp-central-1/artifacts/8iswuvo5_Email-Signature-2024.jpg"
                  alt="Joanne Reilly Property - Agent of the Year 2024 Twin Waters"
                  className="max-w-full max-h-full w-auto h-auto object-contain"
                />
              </a>

              {/* Bendigo Bank */}
              <a 
                href="https://www.bendigobank.com.au/branch/qld/community-bank-marcoola/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center justify-center p-5 bg-slate-50 rounded-xl border border-slate-200 hover:border-cyan-300 hover:shadow-lg transition-all duration-300 h-44"
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_comp-central-1/artifacts/tsqdn13x_BendigoBank_original.jpg"
                  alt="Bendigo Bank - Marcoola Community Bank Branch"
                  className="max-w-full max-h-full w-auto h-auto object-contain"
                />
              </a>

              {/* Lever Law */}
              <a
                href="https://leverlaw.com.au/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center p-5 bg-slate-50 rounded-xl border border-slate-200 hover:border-cyan-300 hover:shadow-lg transition-all duration-300 h-44"
              >
                <img
                  src="/sponsors/lever-law.jpeg"
                  alt="Lever Law - Proud Sponsor of Paws in the Park 2026"
                  className="max-w-full max-h-full w-auto h-auto object-contain"
                />
              </a>

              {/* Sunshine Coast Council */}
              <a 
                href="https://www.sunshinecoast.qld.gov.au" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center justify-center p-5 bg-slate-50 rounded-xl border border-slate-200 hover:border-cyan-300 hover:shadow-lg transition-all duration-300 h-44"
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_comp-central-1/artifacts/g6dm3u3g_scc_logo_customblue_bg_portal.jpg"
                  alt="Sunshine Coast Council"
                  className="max-w-full max-h-full w-auto h-auto object-contain"
                />
              </a>

              {/* No More Toads (Toad Busters) */}
              <a 
                href="https://www.nomoretoads.com.au" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center justify-center p-5 bg-slate-50 rounded-xl border border-slate-200 hover:border-cyan-300 hover:shadow-lg transition-all duration-300 h-44"
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_comp-central-1/artifacts/il4qz3k8_NMT%20Mascot%20roundel%20copy.jpeg"
                  alt="No More Toads"
                  className="max-w-full max-h-full w-auto h-auto object-contain"
                />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section — Paw-genda */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight mb-3">
              What's On This Year's Paw-genda
            </h2>
            <p className="text-base md:text-lg text-slate-600">
              Four reasons to grab the lead and head down to Warwick Daniels Lake View Park on 6 September.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { ...features[0], link: '/competitions', showLearnMore: true, isHeroDog: true },
              { ...features[1], link: null, showLearnMore: false },
              { ...features[2], link: '/performers', showLearnMore: true },
              { ...features[3], link: '/competitions', showLearnMore: true }
            ].map((feature) => {
              const Icon = feature.icon;
              // For the Hero Dog card we render nested links inside, so the outer
              // wrapper must be a <div> (with role="link") to avoid invalid nested <a> tags.
              const useOuterLink = feature.link && !feature.isHeroDog;
              const CardWrapper = useOuterLink ? Link : 'div';
              let wrapperProps = useOuterLink ? { to: feature.link } : {};
              if (feature.isHeroDog) {
                wrapperProps = {
                  role: 'link',
                  tabIndex: 0,
                  onClick: () => navigate(feature.link),
                  onKeyDown: (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(feature.link);
                    }
                  },
                };
              }

              return (
                <CardWrapper
                  key={feature.title} 
                  {...wrapperProps}
                  className={feature.link ? "group cursor-pointer" : ""}
                >
                  <Card className={`border-slate-200 transition-all duration-300 h-full overflow-hidden ${feature.link ? 'hover:border-cyan-300 hover:shadow-xl hover:-translate-y-1' : ''}`}>
                    {feature.image && (
                      <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                        <img
                          src={feature.image}
                          alt={feature.title}
                          className={`w-full h-full object-cover ${feature.imagePos || 'object-center'} transition-transform duration-500 group-hover:scale-105`}
                        />
                      </div>
                    )}
                    <CardHeader>
                      <div className={`bg-gradient-to-br from-cyan-100 to-teal-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4 transition-all duration-300 ${feature.link ? 'group-hover:from-cyan-600 group-hover:to-teal-600' : ''}`}>
                        <Icon className={`h-6 w-6 text-cyan-700 transition-colors duration-300 ${feature.link ? 'group-hover:text-white' : ''}`} />
                      </div>
                      <CardTitle className={`text-xl transition-colors ${feature.link ? 'group-hover:text-cyan-700' : ''}`}>{feature.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-slate-600">
                        {feature.isHeroDog ? (
                          <span>
                            Enter competitions for{' '}
                            <Link 
                              to="/competitions#everyday-hero" 
                              className="text-cyan-600 hover:text-cyan-700 underline font-semibold"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Twin Waters Everyday Hero
                            </Link>
                            {' '}and{' '}
                            <Link 
                              to="/competitions#dog-dad" 
                              className="text-cyan-600 hover:text-cyan-700 underline font-semibold"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Best Dog Dad
                            </Link>
                          </span>
                        ) : (
                          feature.description
                        )}
                      </CardDescription>
                      {feature.showLearnMore && (
                        <div className="mt-4 flex items-center text-cyan-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                          Learn More <ArrowRight className="ml-1 h-4 w-4" />
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </CardWrapper>
              );
            })}
          </div>
        </div>
      </section>

      {/* Visual Banner Section */}
      <section className="py-16 bg-gradient-to-r from-slate-900 to-slate-800 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://images.pexels.com/photos/27412882/pexels-photo-27412882.jpeg?auto=compress&cs=tinysrgb&w=1920"
            alt="Background pattern"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 tracking-tight">
              Join the Celebration of Hero Dogs
            </h2>
            <p className="text-xl text-slate-300 mb-8">
              A day dedicated to honoring the remarkable dogs who serve, protect, and bring joy to our lives
            </p>
            <div className="flex flex-wrap justify-center gap-8 text-center">
              <div>
                <div className="text-4xl font-bold text-teal-400 mb-2">5+</div>
                <div className="text-slate-300">Service Groups</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-cyan-400 mb-2">6</div>
                <div className="text-slate-300">Fun Competitions</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-teal-400 mb-2">10+</div>
                <div className="text-slate-300">Activities & Vendors</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Last Year's Event Highlights */}
      <section className="py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-4 tracking-tight">
              Memories from Paws in the Park 2025
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Relive the joy, laughter, and paws-itivity from last year's event.
            </p>
          </div>

          <div className="max-w-7xl mx-auto">
            {/* Horizontal scroll carousel */}
            <div className="relative -mx-4 md:-mx-0">
              {/* Left arrow */}
              <button
                type="button"
                onClick={() => scrollMemories('left')}
                aria-label="Scroll memories left"
                className="hidden md:flex absolute left-2 top-1/2 -translate-y-1/2 z-10 h-12 w-12 items-center justify-center rounded-full bg-white/95 backdrop-blur border border-slate-200 shadow-lg hover:bg-white hover:scale-110 active:scale-95 transition-all"
                data-testid="memories-scroll-left"
              >
                <ArrowLeft className="h-5 w-5 text-slate-700" />
              </button>

              {/* Right arrow */}
              <button
                type="button"
                onClick={() => scrollMemories('right')}
                aria-label="Scroll memories right"
                className="hidden md:flex absolute right-2 top-1/2 -translate-y-1/2 z-10 h-12 w-12 items-center justify-center rounded-full bg-white/95 backdrop-blur border border-slate-200 shadow-lg hover:bg-white hover:scale-110 active:scale-95 transition-all"
                data-testid="memories-scroll-right"
              >
                <ArrowRight className="h-5 w-5 text-slate-700" />
              </button>

              <div
                ref={memoriesRef}
                className="flex gap-5 overflow-x-auto snap-x snap-mandatory hide-scrollbar px-4 md:px-0 pb-4 scroll-smooth"
              >
                {[
                  { src: "https://customer-assets.emergentagent.com/job_comp-central-1/artifacts/e7jy5pyu_1000012434.jpg", title: "Costume Parade", caption: "Heavenly hounds at last year's parade!", alt: "Westie in angel wings" },
                  { src: "https://customer-assets.emergentagent.com/job_comp-central-1/artifacts/d6q712h1_1A119A12-E220-49D6-B71F-A8F57DCE356A_1_105_c.jpeg", title: "Hero Heroes", caption: "Striking portraits of our four-legged stars", alt: "Airedale Terrier portrait" },
                  { src: "https://customer-assets.emergentagent.com/job_comp-central-1/artifacts/z3ho2o1e_9D9FF293-9868-439C-89AB-17535414297D_1_105_c.jpeg", title: "Best in Denim", caption: "Pure smiles and the sweetest denim outfit", alt: "Small dog in denim jacket and cap held by smiling owner" },
                  { src: "https://customer-assets.emergentagent.com/job_comp-central-1/artifacts/bngyt8ni_2B6A3ACB-78EC-4CA0-A0CB-8C7AA66690DF_1_105_c.jpeg", title: "Vendor Village", caption: "Story Dogs, Canine Perspective and more amazing stalls", alt: "Vendor village in full swing" },
                  { src: "https://customer-assets.emergentagent.com/job_comp-central-1/artifacts/52hnjdqz_3B1A4A20-264C-472A-97EE-0B2A23B71ED9_1_105_c.jpeg", title: "Competition Winners", caption: "Royalty on four paws — last year's stars!", alt: "Costume winner in red velvet" },
                  { src: "https://customer-assets.emergentagent.com/job_comp-central-1/artifacts/ciy2u8fw_5E0A3DEC-D1E1-4423-9600-F7D17055736A_1_105_c.jpeg", title: "Community Spirit", caption: "Our amazing volunteers make the day possible", alt: "Volunteers in hi-vis vests" },
                  { src: "https://customer-assets.emergentagent.com/job_comp-central-1/artifacts/pbubby4o_B9E2B441-A0E8-4BCF-B12A-5A3013ECF0E0_1_105_c.jpeg", title: "Cutest Costumes", caption: "A peapod in pink — pure parade magic", alt: "Peapod costume" },
                  { src: "https://customer-assets.emergentagent.com/job_comp-central-1/artifacts/ph6simjm_5709A615-49D1-4EBD-BF90-C5FFFB4C27E6_1_105_c.jpeg", title: "Agility Ring", caption: "Team colours, ringside grins", alt: "Handler with spaniel in agility ring" },
                ].map((item) => (
                  <div
                    key={item.src}
                    className="snap-start flex-shrink-0 w-[80%] sm:w-[55%] md:w-[40%] lg:w-[28%] relative group overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300"
                  >
                    <img
                      src={item.src}
                      alt={item.alt}
                      className="w-full h-80 md:h-96 object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    {/* Permanent gradient + always-visible title */}
                    <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-black/85 via-black/45 to-transparent">
                      <h3 className="text-white text-xl font-bold mb-1 tracking-tight">{item.title}</h3>
                      <p className="text-white/85 text-sm leading-relaxed">{item.caption}</p>
                    </div>
                  </div>
                ))}
              </div>
              {/* Edge fade indicators */}
              <div className="hidden md:block absolute top-0 right-0 bottom-4 w-12 bg-gradient-to-l from-slate-50 to-transparent pointer-events-none"></div>
              <p className="text-center text-sm text-slate-500 italic mt-2">
                Use the arrows or swipe to browse memories
              </p>
            </div>

            <div className="text-center mt-8">
              <Button asChild size="lg" className="bg-gradient-to-r from-cyan-600 to-teal-600 hover:from-cyan-700 hover:to-teal-700">
                <Link to="/gallery">
                  View Full Gallery
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Community Partner Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-cyan-50 to-teal-50 rounded-2xl p-8 md:p-12 border border-cyan-200">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-slate-900 mb-4">
                  Organized by Twin Waters Residents Association
                </h2>
                <p className="text-lg text-slate-600 mb-6">
                  TWRA is dedicated to building a vibrant, connected community through meaningful events and initiatives
                </p>
                <div className="mb-8">
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">
                    Follow Our Journey
                  </h3>
                  <div className="flex flex-wrap justify-center gap-4">
                    <Button asChild size="lg" className="bg-gradient-to-r from-cyan-600 to-teal-600 hover:from-cyan-700 hover:to-teal-700">
                      <a href="https://twra.net" target="_blank" rel="noopener noreferrer">
                        Visit TWRA.net
                      </a>
                    </Button>
                    <Button asChild size="lg" variant="outline" className="border-cyan-600 text-cyan-700 hover:bg-cyan-50">
                      <a href="https://www.instagram.com/twin_waters_res_assoc/" target="_blank" rel="noopener noreferrer">
                        Follow on Instagram
                      </a>
                    </Button>
                    <Button asChild size="lg" variant="outline" className="border-blue-600 text-blue-700 hover:bg-blue-50">
                      <a href="https://www.facebook.com/TwinWatersResidentsAssociation/" target="_blank" rel="noopener noreferrer">
                        Follow on Facebook
                      </a>
                    </Button>
                  </div>
                </div>
              </div>

              <div className="space-y-4 bg-white/50 backdrop-blur rounded-xl p-6">
                <Card className="border-l-4 border-l-cyan-600">
                  <CardHeader>
                    <CardTitle className="text-lg">Event Highlights</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 text-slate-600">
                      <li className="flex items-start">
                        <span className="text-cyan-600 mr-2">•</span>
                        Hero Dog Competitions & Awards
                      </li>
                      <li className="flex items-start">
                        <span className="text-cyan-600 mr-2">•</span>
                        Fashion Parade with Heroes Theme
                      </li>
                      <li className="flex items-start">
                        <span className="text-cyan-600 mr-2">•</span>
                        Live demonstrations
                      </li>
                      <li className="flex items-start">
                        <span className="text-cyan-600 mr-2">•</span>
                        Food, Coffee & Dog Treats
                      </li>
                      <li className="flex items-start">
                        <span className="text-cyan-600 mr-2">•</span>
                        Ice cream van for humans & dogs
                      </li>
                      <li className="flex items-start">
                        <span className="text-cyan-600 mr-2">•</span>
                        Dog Photo Booth
                      </li>
                      <li className="flex items-start">
                        <span className="text-cyan-600 mr-2">•</span>
                        Complimentary dog nail trims
                      </li>
                      <li className="flex items-start">
                        <span className="text-cyan-600 mr-2">•</span>
                        Raffle Draw with Prizes
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-cyan-50 to-teal-50 border-cyan-200">
                  <CardHeader>
                    <CardTitle className="text-lg">TWRA Paws in the Park Subcommittee</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm text-slate-700">
                      {committee.map((member) => (
                        <div key={member.name} className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-cyan-600 rounded-full flex-shrink-0"></div>
                          <span>{member.name}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Event Etiquette Section */}
      <section className="py-16 bg-white border-t border-slate-200">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <Badge className="mb-4 bg-cyan-100 text-cyan-800 hover:bg-cyan-200 border-cyan-200">
                Important Information
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 tracking-tight">
                Event Etiquette & Safety
              </h2>
              <p className="text-lg text-slate-600 max-w-3xl mx-auto">
                Help us create a safe, respectful experience for all attendees with our Traffic Light Ribbon System and event guidelines
              </p>
            </div>

            {/* Traffic Light Ribbon Preview */}
            <div className="mb-8">
              <img
                src="https://customer-assets.emergentagent.com/job_comp-central-1/artifacts/oi8z3jxh_Screenshot%202026-05-17%20at%2011.06.27%E2%80%AFam.png"
                alt="Traffic Light Ribbon System"
                className="w-full max-w-4xl mx-auto rounded-xl shadow-xl border border-slate-200"
              />
            </div>

            {/* Quick Guidelines */}
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <Card className="border-slate-200">
                <CardContent className="pt-6">
                  <div className="flex items-center space-x-2 mb-3">
                    <div className="w-3 h-3 bg-cyan-600 rounded-full"></div>
                    <h3 className="font-semibold text-slate-900">Keep Dogs Leashed</h3>
                  </div>
                  <p className="text-sm text-slate-600">All dogs must remain on leash throughout the event</p>
                </CardContent>
              </Card>

              <Card className="border-slate-200">
                <CardContent className="pt-6">
                  <div className="flex items-center space-x-2 mb-3">
                    <div className="w-3 h-3 bg-cyan-600 rounded-full"></div>
                    <h3 className="font-semibold text-slate-900">Respect Space</h3>
                  </div>
                  <p className="text-sm text-slate-600">Use ribbon system to understand each dog's comfort level</p>
                </CardContent>
              </Card>

              <Card className="border-slate-200">
                <CardContent className="pt-6">
                  <div className="flex items-center space-x-2 mb-3">
                    <div className="w-3 h-3 bg-cyan-600 rounded-full"></div>
                    <h3 className="font-semibold text-slate-900">Stay Hydrated</h3>
                  </div>
                  <p className="text-sm text-slate-600">Water stations available for you and your furry friend</p>
                </CardContent>
              </Card>
            </div>

            <div className="text-center">
              <Button asChild size="lg" className="bg-gradient-to-r from-cyan-600 to-teal-600 hover:from-cyan-700 hover:to-teal-700">
                <Link to="/etiquette">
                  View Full Event Etiquette Guidelines
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-10 md:py-12 bg-gradient-to-r from-cyan-600 to-teal-600 text-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 max-w-5xl mx-auto text-center md:text-left">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-1">Ready to Join Us?</h2>
              <p className="text-base text-cyan-50">Register your dog or drop us a line.</p>
            </div>
            <div className="flex flex-wrap justify-center gap-3">
              <Button asChild size="default" variant="secondary" className="bg-white text-cyan-700 hover:bg-slate-100">
                <Link to="/competitions">Register Your Dog</Link>
              </Button>
              <Button asChild size="default" variant="outline" className="border-white text-white hover:bg-white/10">
                <a href="mailto:paws@twra.net">Email paws@twra.net</a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
